/**
 * Объявления платформы (ТЗ §26.31.11).
 *
 * Критическое сообщение показывается первым, но ничего не перекрывает:
 * баннер встроен в поток страницы, а не висит поверх ответа или таймера.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, Info, Megaphone, Wrench, X } from 'lucide-react'

import { useBannerClicked, useBannerDismissed, useBanners } from '@/features/site/api'
import type { BannerKind, BannerPlacement, PublicBanner } from '@/entities/site/types'
import { cn } from '@/shared/lib/cn'

const KIND_STYLES: Record<BannerKind, string> = {
  info: 'border-line',
  update: 'border-accent/50',
  warning: 'border-warning/60',
  maintenance: 'border-warning/60',
  critical: 'border-danger',
}

const KIND_ICONS: Record<BannerKind, typeof Info> = {
  info: Info,
  update: Megaphone,
  warning: AlertTriangle,
  maintenance: Wrench,
  critical: AlertTriangle,
}

/** Что ученик уже закрыл — помним локально, чтобы не навязывать снова. */
function dismissedKey(id: string) {
  return `ut:banner:${id}:dismissed`
}

function wasDismissed(id: string): boolean {
  try {
    return sessionStorage.getItem(dismissedKey(id)) === '1'
  } catch {
    return false
  }
}

export function BannerStrip({ placement }: { placement: BannerPlacement }) {
  const banners = useBanners(placement)
  const dismissed = useBannerDismissed()
  const clicked = useBannerClicked()
  const [hidden, setHidden] = useState<string[]>([])

  useEffect(() => {
    const rows = banners.data ?? []
    const already = rows.filter((row) => wasDismissed(row.id)).map((row) => row.id)
    if (already.length > 0) setHidden((previous) => [...new Set([...previous, ...already])])
  }, [banners.data])

  const rows = (banners.data ?? []).filter((row) => !hidden.includes(row.id))
  if (rows.length === 0) return null

  const close = (banner: PublicBanner) => {
    setHidden((previous) => [...previous, banner.id])
    try {
      sessionStorage.setItem(dismissedKey(banner.id), '1')
    } catch {
      // приватный режим — закрытие просто не запомнится
    }
    void dismissed.mutateAsync(banner.id)
  }

  return (
    <div className="flex flex-col gap-2">
      {rows.map((banner) => {
        const Icon = KIND_ICONS[banner.kind]
        return (
          <div
            key={banner.id}
            role={banner.kind === 'critical' ? 'alert' : 'status'}
            className={cn(
              'flex items-start gap-3 rounded-lg border bg-surface p-3',
              KIND_STYLES[banner.kind],
            )}
          >
            <Icon
              aria-hidden
              className={cn(
                'mt-0.5 size-4 shrink-0',
                banner.kind === 'critical' ? 'text-danger' : 'text-ink-muted',
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-meta font-medium text-ink">{banner.title}</p>
              {banner.body && <p className="mt-0.5 text-meta text-ink-soft">{banner.body}</p>}
              {banner.action_label && banner.action_url && (
                <a
                  href={banner.action_url}
                  {...(banner.action_url.startsWith('http')
                    ? { target: '_blank', rel: 'noreferrer' }
                    : {})}
                  onClick={() => void clicked.mutateAsync(banner.id)}
                  className="mt-1.5 inline-block text-meta font-medium text-accent underline-offset-4 hover:underline"
                >
                  {banner.action_label}
                </a>
              )}
            </div>
            {banner.dismissible && (
              <button
                type="button"
                onClick={() => close(banner)}
                aria-label="Скрыть объявление"
                className="-m-1.5 flex size-11 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-raised hover:text-ink"
              >
                <X aria-hidden className="size-4" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
